// SCR-STK-01 Inventory, SCR-STK-08 reconciliation and SCR-STK-02 product stock (frozen mockups-stk-v2, ADR-087).
// Tabs: All · Garage · Trucks · Damaged; reconciliation opens from "Ledger check" (ADR-104). Owner and Storekeeper act; a Seller only views (trucks come in P5).
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Chips,
  EmptyState,
  List,
  ListRow,
  Notice,
  QuickActions,
  SearchField,
  Section,
  SkeletonList,
  StockTile,
  SummaryCard,
  Tabs,
  Tiles,
  type QuickAction,
  VirtualList,
} from '../../design/kit';
import {
  loadInventory,
  loadMovements,
  loadOpeningOpen,
  loadStockCheck,
  type InventoryRow,
  type Movement,
} from '../../data/stock';
import { Name } from '../../i18n/Isolate';
import type { Language } from '../../i18n';
import { formatDateTime } from '../../lib/format';
import { useSession } from '../../app/session';
import { useCardHeader } from '../../app/Shell';
import { useProductPhoto, useTab } from '../catalog/common';
import { loadTruckStock } from '../../data/rounds';
import { loadLots } from '../../data/advanced';
import { LOTS } from '../catalog/AdvancedSheets';
import { useTrucks } from '../rounds/TrucksPage';
import { MoveSheet, TruckCountSheet } from '../rounds/RoundSheets';
import { AdjustSheet, CountSheet, OpeningSheet, RepackSheet, STOCK, WriteOffSheet, useStockWords } from './StockSheets';

const TABS = ['all', 'garage', 'trucks', 'damaged'] as const;
type Tab = (typeof TABS)[number];
type Sheet = 'adjust' | 'count' | 'repack' | 'writeoff' | 'opening' | null;
const MOVE_KEY: Record<Movement['kind'], string> = {
  opening: 'move.opening',
  adjust: 'move.adjust',
  count: 'move.count',
  writeoff: 'move.writeoff',
  repack: 'move.repack',
  damaged_in: 'move.damaged',
  receipt: 'move.receipt',
  supplier_return: 'move.supreturn',
  load: 'move.load',
  unload: 'move.unload',
  truck_count: 'move.truckcount',
  sale: 'move.sale',
  box_open: 'move.boxopen',
};
const MOVE_ICON = {
  opening: 'layers',
  adjust: 'swap',
  count: 'check',
  writeoff: 'alert',
  repack: 'box',
  damaged_in: 'alert',
  receipt: 'cart',
  supplier_return: 'return',
  load: 'truck',
  unload: 'truck',
  truck_count: 'check',
  sale: 'cart',
  box_open: 'box',
} as const;

export function useInventory() {
  return useQuery({ queryKey: [...STOCK, 'inventory'], queryFn: loadInventory });
}

export function InventoryPage() {
  const { t } = useTranslation();
  const { role } = useSession();
  const acts = role === 'owner' || role === 'storekeeper';
  const photo = useProductPhoto();
  const navigate = useNavigate();
  const [tab, setTab] = useTab(TABS, 'all');
  const [sheet, setSheet] = useState<Sheet>(null);
  const [text, setText] = useState('');
  const inventory = useInventory();
  const trucks = useTrucks();
  // SCR-ADV-04: expired lots still in stock, for the owner and the Storekeeper (ADR-123).
  const lots = useQuery({ queryKey: [...LOTS, 'all'], queryFn: () => loadLots(), enabled: acts });
  const expired = (lots.data ?? []).filter((l) => l.expired && l.pieces > 0);
  // Every truck's stock per product, for the All tab (INV-01: business total with the split).
  const onTrucks = useMemo(() => {
    const m = new Map<string, { boxes: number; loose: number; calc: number }>();
    for (const k of trucks.data ?? [])
      for (const x of k.stock) {
        const o = m.get(x.product_id) ?? { boxes: 0, loose: 0, calc: 0 };
        m.set(x.product_id, {
          boxes: o.boxes + x.boxes,
          loose: o.loose + x.loose,
          calc: o.calc + x.boxes * (x.box_capacity ?? 0) + x.loose,
        });
      }
    return m;
  }, [trucks.data]);
  const opening = useQuery({ queryKey: [...STOCK, 'opening'], queryFn: loadOpeningOpen, enabled: role === 'owner' });
  const words = useStockWords();
  const all = useMemo(() => inventory.data ?? [], [inventory.data]);
  const at = (kind: 'garage' | 'damaged') => all.filter((r) => r.location === kind);
  const close = () => setSheet(null);

  const actions: QuickAction[] = !acts
    ? []
    : tab === 'garage'
      ? [
          { id: 'adjust', label: t('act.adjust'), icon: 'swap', main: true, onSelect: () => setSheet('adjust') },
          { id: 'count', label: t('act.count'), icon: 'check', onSelect: () => setSheet('count') },
          { id: 'repack', label: t('act.repack'), icon: 'box', onSelect: () => setSheet('repack') },
        ]
      : tab === 'damaged'
        ? [
            {
              id: 'writeoff',
              label: t('act.writeoff'),
              icon: 'alert',
              main: true,
              onSelect: () => setSheet('writeoff'),
            },
          ]
        : tab === 'all'
          ? [
              ...(role === 'owner' && opening.data
                ? [
                    {
                      id: 'opening',
                      label: t('act.opening'),
                      icon: 'layers' as const,
                      main: true,
                      onSelect: () => setSheet('opening'),
                    },
                  ]
                : []),
              { id: 'ledger', label: t('inv.ledger'), icon: 'check', onSelect: () => navigate('/inventory/reconcile') },
            ]
          : [];

  // One tile per product: on All, the sum of garage and damaged with the split; elsewhere that location only.
  const tiles = useMemo(() => {
    const q = text.trim().toLowerCase();
    const garage = all.filter((r) => r.location === 'garage');
    return garage
      .filter((g) => !q || g.name.toLowerCase().includes(q) || (g.sku ?? '').toLowerCase().includes(q))
      .map((g) => {
        const d = all.find((r) => r.product_id === g.product_id && r.location === 'damaged');
        const shown = tab === 'damaged' ? (d ?? g) : g;
        const k = onTrucks.get(g.product_id) ?? { boxes: 0, loose: 0, calc: 0 };
        const boxes = tab === 'all' ? g.boxes + (d?.boxes ?? 0) + k.boxes : shown.boxes;
        const loose = tab === 'all' ? g.loose + (d?.loose ?? 0) + k.loose : shown.loose;
        const calc = tab === 'all' ? g.calc_pieces + (d?.calc_pieces ?? 0) + k.calc : shown.calc_pieces;
        return { row: g, boxes, loose, calc, g, d, k };
      })
      .filter((x) => (tab === 'damaged' ? x.calc > 0 : x.row.active || x.calc > 0));
  }, [all, text, tab, onTrucks]);

  if (inventory.isError) return <Notice tone="danger">{t('err.generic')}</Notice>;
  const truckTotal = [...onTrucks.values()].reduce((s, n) => s + n.calc, 0);
  const total = all.reduce((s, r) => s + r.calc_pieces, 0) + truckTotal;
  const garageTotal = at('garage').reduce((s, r) => s + r.calc_pieces, 0);

  return (
    <>
      {expired.length > 0 && (
        <Notice tone="warning">
          {t('lot.notice', { n: expired.length, p: expired.reduce((n, l) => n + l.pieces, 0) })} ·{' '}
          <Name>{expired[0]?.product}</Name>{' '}
          <Button pill onClick={() => navigate(`/products/${expired[0]?.product_id ?? ''}?tab=lots`)}>
            {t('lot.open')}
          </Button>
        </Notice>
      )}
      {tab === 'all' && inventory.data && (
        <SummaryCard
          label={t('stock.total')}
          value={t('pieces.n', { n: total })}
          figures={[
            { label: t('loc.garage'), value: t('pieces.n', { n: garageTotal }) },
            { label: t('tab.trucks'), value: t('pieces.n', { n: truckTotal }) },
            { label: t('loc.damaged'), value: t('pieces.n', { n: total - garageTotal - truckTotal }) },
          ]}
        />
      )}
      <Tabs
        label={t('p.inventory')}
        value={tab}
        onChange={(v) => setTab(v as Tab)}
        items={TABS.map((id) => ({ id, label: t(`tab.${id}`) }))}
      />
      {tab === 'trucks' ? (
        <TrucksTab />
      ) : (
        <>
          <QuickActions actions={actions} />
          <SearchField value={text} onChange={setText} label={t('search.stock')} />
          {!inventory.data ? (
            <SkeletonList label={t('loading')} />
          ) : tiles.length === 0 ? (
            <EmptyState icon="layers" title={t('empty.loc')} text={t('empty.loc.text')} />
          ) : (
            <Tiles label={t(`tab.${tab}`)}>
              {tiles.map((x) => (
                <StockTile
                  key={x.row.product_id}
                  testId={`tile-${x.row.sku ?? x.row.product_id}`}
                  image={photo(x.row.product_id)}
                  name={<Name>{x.row.name}</Name>}
                  quantity={words.qty(x.row, x.boxes, x.loose)}
                  empty={x.calc === 0}
                  onOpen={() => navigate(`/inventory/${x.row.product_id}`)}
                  sub={
                    tab === 'all'
                      ? t('tile.split3', { g: x.g.calc_pieces, k: x.k.calc, d: x.d?.calc_pieces ?? 0 })
                      : `${t('calc.pieces', { n: x.calc })}${x.row.box_capacity !== null ? ` · ${t('boxof', { n: x.row.box_capacity })}` : ''}`
                  }
                />
              ))}
            </Tiles>
          )}
          {tab === 'all' && <LatestMovements />}
        </>
      )}
      {sheet === 'adjust' && <AdjustSheet garage={at('garage').filter((r) => r.active)} onClose={close} />}
      {sheet === 'count' && (
        <CountSheet garage={at('garage').filter((r) => r.active || r.calc_pieces > 0)} onClose={close} />
      )}
      {sheet === 'repack' && <RepackSheet garage={at('garage')} onClose={close} />}
      {sheet === 'writeoff' && <WriteOffSheet damaged={at('damaged')} onClose={close} />}
      {sheet === 'opening' && <OpeningSheet products={at('garage').filter((r) => r.active)} onClose={close} />}
    </>
  );
}

function MovementRows({ movements, product }: { movements: Movement[]; product?: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const words = useStockWords();
  return (
    <VirtualList
      label={t('moves.latest')}
      items={movements}
      rowKey={(m) => m.id}
      row={(m) => (
        <ListRow
          testId={`move-${m.id}`}
          lead={{ icon: MOVE_ICON[m.kind] }}
          title={
            m.kind === 'damaged_in'
              ? t(MOVE_KEY[m.kind])
              : `${t(MOVE_KEY[m.kind])} · ${t(`loc.${m.lines[0]?.location ?? 'garage'}`)}`
          }
          sub={
            <>
              {formatDateTime(m.at, lang)}
              {m.actor_name && (
                <>
                  {' · '}
                  <Name>{m.actor_name}</Name>
                </>
              )}
              {m.reason && ` · ${m.reason}`}
              {m.lines
                .filter((l) => !product || l.product_id === product)
                .map((l) => (
                  <span key={`${l.product_id}-${l.location}`}>
                    <br />
                    <Name>{l.product}</Name> {words.signed(l, l.boxes, l.loose)}
                    {l.reason && !m.reason && ` · ${l.reason}`}
                  </span>
                ))}
            </>
          }
        />
      )}
    />
  );
}

function LatestMovements() {
  const { t } = useTranslation();
  const moves = useQuery({ queryKey: [...STOCK, 'movements'], queryFn: () => loadMovements(12) });
  return (
    <Section title={t('moves.latest')}>
      {!moves.data ? (
        <SkeletonList label={t('loading')} />
      ) : moves.data.length === 0 ? (
        <Notice tone="info">{t('history.empty.text')}</Notice>
      ) : (
        <MovementRows movements={moves.data} />
      )}
    </Section>
  );
}

function Reconciliation() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const words = useStockWords();
  const check = useQuery({ queryKey: [...STOCK, 'check'], queryFn: loadStockCheck });
  if (!check.data) return <SkeletonList label={t('loading')} />;
  const diffs = check.data.filter((c) => !c.matched);
  return (
    <>
      <SummaryCard
        plain
        label={t('rec.checked', { n: check.data.length })}
        value={diffs.length ? t('lines.diff', { n: diffs.length }) : t('rec.matched')}
        sub={t('rec.at', { t: formatDateTime(new Date(check.dataUpdatedAt), lang) })}
      />
      {diffs.length === 0 ? (
        <Notice tone="info">{t('rec.allok')}</Notice>
      ) : (
        <Notice tone="danger">{t('rec.diff')}</Notice>
      )}
      <List label={t('tab.reconcile')}>
        {check.data.map((c) => (
          <ListRow
            key={`${c.product_id}-${c.location}`}
            testId={`check-${c.name}-${c.location}`}
            lead={{ icon: c.matched ? 'check' : 'alert' }}
            title={
              <>
                <Name>{c.name}</Name> · {t(`loc.${c.location}`)}
              </>
            }
            sub={
              c.matched ? undefined : (
                <>
                  {t('rec.stored')}{' '}
                  {words
                    .signed({ box_capacity: c.stored_boxes ? 1 : null }, c.stored_boxes, c.stored_loose)
                    .replace(/\+/g, '')}{' '}
                  · {t('rec.recomputed')}{' '}
                  {words
                    .signed({ box_capacity: c.ledger_boxes ? 1 : null }, c.ledger_boxes, c.ledger_loose)
                    .replace(/\+/g, '')}
                </>
              )
            }
            badge={<Badge tone={c.matched ? 'success' : 'danger'}>{t(c.matched ? 'rec.matched' : 'rec.diff')}</Badge>}
          />
        ))}
      </List>
    </>
  );
}

/** SCR-STK-02: one product's balance per location and its movements. */
export function StockCard() {
  const { t } = useTranslation();
  const { id: product = '' } = useParams();
  const navigate = useNavigate();
  const words = useStockWords();
  const [tab, setTab] = useTab(['balance', 'moves'] as const, 'balance');
  const inventory = useInventory();
  const rows: InventoryRow[] = (inventory.data ?? []).filter((r) => r.product_id === product);
  const first = rows[0];
  const moves = useQuery({
    queryKey: [...STOCK, 'movements', product],
    queryFn: () => loadMovements(200, product),
    enabled: tab === 'moves',
  });
  const back = useCallback(() => navigate('/inventory'), [navigate]);
  useCardHeader(first ? <Name>{first.name}</Name> : t('p.inventory'), back);
  if (inventory.isLoading) return <SkeletonList label={t('loading')} />;
  if (!first) return <Notice tone="warning">{t('err.notfound')}</Notice>;
  const total = rows.reduce((s, r) => s + r.calc_pieces, 0);
  return (
    <>
      <SummaryCard label={`${t('loc.total')} (${t('calc')})`} value={t('pieces.n', { n: total })} />
      <Tabs
        label={t('s22.title')}
        value={tab}
        onChange={(v) => setTab(v as 'balance' | 'moves')}
        items={[
          { id: 'balance', label: t('tab.balance') },
          { id: 'moves', label: t('tab.moves') },
        ]}
      />
      {tab === 'balance' ? (
        <List label={t('tab.balance')}>
          {rows.map((r) => (
            <ListRow
              key={r.location}
              testId={`balance-${r.location}`}
              lead={{ icon: r.location === 'garage' ? 'layers' : 'alert' }}
              title={t(`loc.${r.location}`)}
              sub={`${t('calc.pieces', { n: r.calc_pieces })}${r.box_capacity !== null ? ` · ${t('boxof', { n: r.box_capacity })}` : ''}`}
              value={words.qty(r, r.boxes, r.loose)}
            />
          ))}
        </List>
      ) : !moves.data ? (
        <SkeletonList label={t('loading')} />
      ) : moves.data.length === 0 ? (
        <EmptyState icon="layers" title={t('history.empty')} text={t('history.empty.text')} />
      ) : (
        <MovementRows movements={moves.data} product={product} />
      )}
    </>
  );
}

/** SCR-RND-07: one truck's stock (truck chips; a Seller sees only theirs), stock movement and truck count. */
function TrucksTab() {
  const { t } = useTranslation();
  const { role, userId } = useSession();
  const words = useStockWords();
  const photo = useProductPhoto();
  const [params, setParams] = useSearchParams();
  const [sheet, setSheet] = useState<'move' | 'count' | null>(null);
  const trucks = useTrucks();
  const inventory = useInventory();
  const mine = (trucks.data ?? []).filter((k) => role !== 'seller' || k.seller_id === userId);
  const truck = mine.find((k) => k.id === params.get('truck')) ?? mine[0];
  const stock = useQuery({
    queryKey: ['rounds', 'stock', truck?.id],
    queryFn: () => loadTruckStock(truck?.id ?? ''),
    enabled: Boolean(truck),
  });
  if (!trucks.data) return <SkeletonList label={t('loading')} />;
  if (!truck) return <EmptyState icon="truck" title={t('rd.notruck')} text={t('rd.notruck.text')} />;
  const pick = (id: string) =>
    setParams(
      (p) => {
        p.set('truck', id);
        return p;
      },
      { replace: true },
    );
  const onTruck = (stock.data ?? []).filter((x) => x.calc_pieces > 0);
  const actions: QuickAction[] = [
    { id: 'move', label: t('act.move'), icon: 'swap', main: true, onSelect: () => setSheet('move') },
    ...(role !== 'seller'
      ? [{ id: 'count', label: t('act.truckcount'), icon: 'check' as const, onSelect: () => setSheet('count') }]
      : []),
  ];
  return (
    <>
      {mine.length > 1 && (
        <Chips
          label={t('tab.trucks')}
          items={mine.map((k) => ({ id: k.id, label: k.code }))}
          selected={[truck.id]}
          onToggle={pick}
        />
      )}
      <QuickActions actions={actions} />
      {!stock.data ? (
        <SkeletonList label={t('loading')} />
      ) : onTruck.length === 0 ? (
        <EmptyState icon="truck" title={t('empty.truckstock')} text={t('empty.truckstock.text')} />
      ) : (
        <Tiles label={t('truck.code', { c: truck.code })}>
          {onTruck.map((x) => (
            <StockTile
              key={x.product_id}
              testId={`ttile-${x.sku ?? x.product_id}`}
              image={photo(x.product_id)}
              name={<Name>{x.name}</Name>}
              quantity={words.qty(x, x.boxes, x.loose)}
              sub={`${t('calc.pieces', { n: x.calc_pieces })}${x.box_capacity !== null ? ` · ${t('boxof', { n: x.box_capacity })}` : ''}`}
            />
          ))}
        </Tiles>
      )}
      {sheet === 'move' && (
        <MoveSheet
          trucks={mine}
          initialTruck={truck.id}
          onTruck={pick}
          garage={(inventory.data ?? []).filter((x) => x.location === 'garage')}
          truckStock={(id) => (id === truck.id ? (stock.data ?? []) : [])}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet === 'count' && <TruckCountSheet truck={truck} rows={stock.data ?? []} onClose={() => setSheet(null)} />}
    </>
  );
}

/** ADR-104: the ledger reconciliation as its own card page, opened from "Ledger check". */
export function ReconcileCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const back = useCallback(() => navigate('/inventory'), [navigate]);
  useCardHeader(t('rec.title'), back);
  return <Reconciliation />;
}
